import '@archetype-themes/scripts/config';
import CartForm from '@archetype-themes/scripts/modules/cart-form';
import '@archetype-themes/scripts/modules/header-nav';
import '@archetype-themes/scripts/helpers/a11y';
import '@archetype-themes/scripts/helpers/utils';
import '@archetype-themes/scripts/helpers/delegate';

/*
  CT: de upsell-rij gebruikt eigen kaartmarkup
  (snippets/ct-cart-upsell-row.liquid) met een eigen Toevoegen-knop die in
  cart-form.js wordt afgehandeld — de theme-modules quick-add, tool-tip en
  product-grid-item zijn hier dus bewust NIET geïmporteerd.

  CT: de cart-markup staat server-side in de drawer
  (snippets/ct-cart-items.liquid via header-cart-drawer.liquid). Er is dus
  geen fetch nodig om de drawer te kunnen openen — ook niet de eerste keer.
  Wat er bij de eerste hover/focus/opening wél gebeurt is het ophalen van de
  S&D-aanbevelingen; zie prepareCart().

  CT: toevoegen (ajaxProduct:added). Het event draagt de verse cart-markup
  uit het antwoord van /cart/add.js mee (Section Rendering API, zie
  product-ajax-form.js). Die wordt toegepast VÓÓRDAT de drawer opent, zodat
  hij nooit met verouderde inhoud openschuift. Eerder opende hij met de
  markup van de pageload — bij een eerste product de lege mand — en kwam de
  inhoud pas na een tweede request.

  CT: er zijn TWEE sluitknoppen (.cart-drawer__close): één in het formulier
  en één in het lege-cart-blok, omdat het formulier bij een lege mand
  verborgen is (snippets/header-cart-drawer.liquid). Sluiten bindt op alle
  .js-close-header-cart; de focus gaat naar de ZICHTBARE knop (_focusInto).
*/

export default class HeaderCart {
  constructor() {
    this.selectors = {
      cartTrigger: '#HeaderCartTrigger',
      cart: '#HeaderCart',
      overlay: '.cart-drawer-overlay',

      closeBtn: '.js-close-header-cart',
      drawerClose: '.cart-drawer__close',
      noteBtn: '.add-note',
      cartItems: '.cart__items',
      liveRegion: '[data-cart-live]'
    };

    this.classes = {
      hidden: 'hide'
    };

    this.config = {
      cartOpen: false,
      cartBuilt: false,
      cartPrepared: false,
      namespace: '.cart-header'
    };

    this.wrapper = document.querySelector(this.selectors.cart);
    if (!this.wrapper) {
      return;
    }
    this.trigger = document.querySelector(this.selectors.cartTrigger);
    this.overlay = document.querySelector(this.selectors.overlay);
    this.noteBtn = this.wrapper.querySelector(this.selectors.noteBtn);
    this.form = this.wrapper.querySelector('form');
    this.liveRegion = this.wrapper.querySelector(this.selectors.liveRegion);
    this.lastFocusedElement = null;

    // A11y: expose the drawer as a modal dialog, labelled by its own title
    // (CT: de titel is visueel verborgen; zie header-cart-drawer.liquid)
    this.wrapper.setAttribute('role', 'dialog');
    this.wrapper.setAttribute('aria-modal', 'true');
    const title = this.wrapper.querySelector('.cart-drawer__title');
    if (title) {
      if (!title.id) {
        title.id = 'HeaderCartTitle';
      }
      this.wrapper.setAttribute('aria-labelledby', title.id);
    } else if (theme.strings.cartLabel) {
      this.wrapper.setAttribute('aria-label', theme.strings.cartLabel);
    }
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    // Close header cart
    document.addEventListener('MobileNav:open', this.close.bind(this));
    document.addEventListener('modalOpen', this.close.bind(this));

    this.init();
  }

  init() {
    this.cartForm = new CartForm(this.form);

    /*
      Performance: nooit een fetch bij pageload.
      De itemlijst is server-side gerenderd, dus de drawer is meteen compleet.
      Ontbreekt die markup toch (oude cache, aangepaste template), dan halen
      we hem alsnog op bij de eerste hover/focus/opening.
    */
    const bubbleNum = document.querySelector('.cart-link__bubble-num');
    const count = bubbleNum ? parseInt(bubbleNum.innerText, 10) || 0 : 0;
    if (count === 0) {
      this.form.parentNode.classList.add('is-empty');
    }
    this.config.cartBuilt = !!this.form.querySelector(this.selectors.cartItems);

    const prepare = function() {
      this.prepareCart();
    }.bind(this);
    if (this.trigger) {
      this.trigger.addEventListener('pointerenter', prepare, { once: true });
      this.trigger.addEventListener('touchstart', prepare, { once: true, passive: true });
      this.trigger.addEventListener('focus', prepare, { once: true });

      this.trigger.on('click', this.open.bind(this));
    }

    document.querySelectorAll(this.selectors.closeBtn).forEach(btn => {
      btn.addEventListener('click', function() {
        this.close();
      }.bind(this));
    });

    /*
      Klik buiten de drawer sluit hem. Dit hangt aan de overlay, niet aan
      window.

      De oude window-handler keek met evt.target.closest('.site-header__cart')
      of de klik van binnen de drawer kwam. Werd de itemlijst net herbouwd,
      dan zat het aangeklikte element al niet meer in de pagina, vond closest()
      niets meer en sloot de drawer terwijl je op een plusknop drukte. De
      overlay ligt per definitie buiten de drawer, dus die vraag hoeft niet
      meer gesteld te worden.
    */
    if (this.overlay) {
      this.overlay.addEventListener('click', function() {
        this.close();
      }.bind(this));
    }

    if (this.noteBtn) {
      this.noteBtn.addEventListener('click', function() {
        this.noteBtn.classList.toggle('is-active');
        this.wrapper.querySelector('.cart__note').classList.toggle('hide');
      }.bind(this));
    }

    /*
      Product toegevoegd (productpagina, quick add, upsell-knop).
      1. detail.handled: cart-form.js heeft de markup al toegepast (upsell).
      2. detail.sections: markup uit het antwoord toepassen — één round trip.
      3. Geen van beide: oude route, volledige rebuild.
      Daarna pas openen, zodat de drawer nooit met oude inhoud openschuift.
    */
    document.addEventListener('ajaxProduct:added', function(evt) {
      this.config.cartBuilt = true;
      const detail = evt.detail || {};

      if (!detail.handled) {
        const applied = this.cartForm.applySections(detail.sections);
        if (applied) {
          this.cartForm.scheduleGiftRebuild();
        } else {
          this.cartForm.buildCart();
        }
      }

      if (!this.config.cartOpen) {
        this.open();
      }
      this.announce();
    }.bind(this));

    /*
      Terug-knop / bfcache: de pagina komt uit het geheugen terug met de cart
      zoals hij was op het moment van weggaan. Die kan inmiddels verouderd
      zijn (ander tabblad, checkout afgebroken), dus dan wel even herbouwen.
    */
    window.addEventListener('pageshow', function(evt) {
      if (evt.persisted) {
        this.config.cartBuilt = true;
        this.cartForm.buildCart();
      }
    }.bind(this));

    // Dev-friendly way to open cart
    document.addEventListener('cart:open', this.open.bind(this));
    document.addEventListener('cart:close', this.close.bind(this));
  }

  /*
    Eenmalig voorbereiden bij de eerste hover/focus/aanraking of opening.
    Staat de itemlijst er al (het normale geval), dan is er niets te bouwen en
    halen we alleen de aanbevelingen op. Ontbreekt de lijst, dan alsnog de
    volledige cart-markup ophalen.
  */
  prepareCart() {
    if (this.config.cartPrepared) {
      return;
    }
    this.config.cartPrepared = true;

    if (!this.config.cartBuilt) {
      this.config.cartBuilt = true;
      this.cartForm.buildCart();
      return;
    }

    const items = this.form.querySelector(this.selectors.cartItems);
    if (items && this.cartForm.updateRecommendations) {
      this.cartForm.updateRecommendations(items.dataset.recProductId, items.dataset.cartProductIds);
    }
  }

  /*
    Screenreader-melding na toevoegen. De tekst staat als data-added-text op
    [data-cart-live] (ct.cart.added, Liquid). Eerst leegmaken en dan in een
    volgend frame vullen, anders leest een screenreader een herhaalde
    melding niet opnieuw voor.
  */
  announce(text) {
    if (!this.liveRegion) {
      return;
    }
    const message = text || this.liveRegion.dataset.addedText || '';
    if (!message) {
      return;
    }
    this.liveRegion.textContent = '';
    window.requestAnimationFrame(function() {
      this.liveRegion.textContent = message;
    }.bind(this));
  }

  open(evt) {
    if (theme.settings.cartType !== 'dropdown') {
      return;
    }

    if (evt) {
      evt.preventDefault();
    }

    this.prepareCart();

    /*
      Bewust GEEN theme.sizeDrawer() meer.
      Die helper hoort bij het oude dropdown-patroon: hij rekende een hoogte
      uit op basis van de headerhoogte en zette die als inline style op de
      drawer. Inline wint van de stylesheet, waardoor het paneel niet de
      volledige viewporthoogte kreeg en er onder de footer ruimte overbleef.
      Ons zijpaneel staat op 100dvh via ct-cart-drawer.css.
    */

    /*
      A11y: onthouden waar de focus vandaan kwam VOORDAT er iets verandert.
      Kwam de focus al uit de drawer (pre-hydratie-opening, zie
      section-header.liquid), dan is de trigger het juiste terugkeerpunt.
    */
    const active = document.activeElement;
    this.lastFocusedElement = (active && !this.wrapper.contains(active)) ? active : this.trigger;

    theme.utils.prepareTransition(this.wrapper, function() {
      this.wrapper.classList.add('is-active');
      this.wrapper.scrollTop = 0;
      this._focusInto();
    }.bind(this));

    document.documentElement.classList.add('cart-open');

    // Lock body scrolling on mobile so the page can't scroll behind the drawer
    if (theme.config.bpSmall) {
      theme.a11y.lockMobileScrolling(this.config.namespace);
    }

    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    // Esc closes cart popup
    window.on('keyup' + this.config.namespace, function(evt) {
      if (evt.keyCode === 27) {
        this.close();
      }
    }.bind(this));

    // Keep keyboard focus inside the drawer while it's open
    window.on('keydown' + this.config.namespace, this._trapFocus.bind(this));

    /*
      Overlay header (homepage): de transparante variant is onleesbaar zolang
      er een lichte overlay overheen ligt, dus die gaat uit. close() zet hem
      terug via restoreOverlayClass().
    */
    if (theme.headerNav && theme.headerNav.removeOverlayClass) {
      theme.headerNav.removeOverlayClass();
    }

    document.dispatchEvent(new CustomEvent('CartDrawer:open'));
    document.dispatchEvent(new CustomEvent('drawerOpen'));

    this.config.cartOpen = true;
  }

  /*
    De zichtbare sluitknop: in het formulier bij een gevulde mand, in het
    lege-cart-blok bij een lege mand (de ander staat op display: none en
    heeft dan geen offsetParent).
  */
  _visibleCloseBtn() {
    const buttons = this.wrapper.querySelectorAll(this.selectors.drawerClose);
    for (let i = 0; i < buttons.length; i++) {
      if (buttons[i].offsetParent !== null) {
        return buttons[i];
      }
    }
    return buttons[0] || null;
  }

  /*
    Focus de dialog in, op de sluitknop.

    Timing is hier alles. Het paneel staat op visibility: hidden met een
    transitie naar visible. Op het exacte startmoment van die transitie
    (t = 0) is de berekende waarde nog 'hidden'; pas daartussen is hij
    'visible'. Een focus() op t = 0 wordt daarom stilletjes genegeerd
    (gevonden in QA). Daarom: eerst een korte timeout zodat de transitie
    loopt, en als vangnet nog een poging op transitionend.
  */
  _focusInto() {
    const attempt = function() {
      if (!this.config.cartOpen && !this.wrapper.classList.contains('is-active')) {
        return;
      }
      if (this.wrapper.contains(document.activeElement)) {
        return;
      }
      const closeBtn = this._visibleCloseBtn();
      if (!closeBtn) {
        return;
      }
      try {
        closeBtn.focus({ preventScroll: true });
      } catch (e) {
        closeBtn.focus();
      }
    }.bind(this);

    window.setTimeout(attempt, 60);

    const onEnd = function(evt) {
      if (evt.target !== this.wrapper) {
        return;
      }
      this.wrapper.removeEventListener('transitionend', onEnd);
      attempt();
    }.bind(this);
    this.wrapper.addEventListener('transitionend', onEnd);
  }

  _trapFocus(evt) {
    if (evt.key !== 'Tab' || !this.config.cartOpen) {
      return;
    }

    /*
      Alleen zichtbare elementen: het formulier (bij een lege mand) of het
      lege-cart-blok (bij een gevulde mand) staat op display: none en die
      knoppen mogen niet in de tab-cirkel.
    */
    const focusable = Array.prototype.filter.call(
      this.wrapper.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      el => el.offsetParent !== null
    );
    if (!focusable.length) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!this.wrapper.contains(document.activeElement)) {
      evt.preventDefault();
      first.focus();
      return;
    }

    if (evt.shiftKey && document.activeElement === first) {
      evt.preventDefault();
      last.focus();
    } else if (!evt.shiftKey && document.activeElement === last) {
      evt.preventDefault();
      first.focus();
    }
  }

  close(evt) {
    if (theme.settings.cartType !== 'dropdown') {
      return;
    }

    if (!this.config.cartOpen) {
      return;
    }

    // If custom event, close without transition
    if (evt && evt.type === 'MobileNav:open') {
      this.wrapper.classList.remove('is-active');
    } else {
      theme.utils.prepareTransition(this.wrapper, function() {
        this.wrapper.classList.remove('is-active');
      }.bind(this));
    }

    window.off('keyup' + this.config.namespace);
    window.off('keydown' + this.config.namespace);

    if (theme.config.bpSmall) {
      theme.a11y.unlockMobileScrolling(this.config.namespace);
    }

    document.documentElement.classList.remove('cart-open');

    /*
      Overlay header terug naar de staat die bij de scrollpositie hoort.
      restoreOverlayClass() doet zelf niets wanneer de header gecomprimeerd is
      of wanneer deze pagina geen overlay header heeft.
    */
    if (theme.headerNav && theme.headerNav.restoreOverlayClass) {
      theme.headerNav.restoreOverlayClass();
    }

    // A11y: restore focus to where the user was before opening
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'false');
    }
    if (this.lastFocusedElement &&
      (this.wrapper.contains(document.activeElement) || document.activeElement === document.body) &&
      document.body.contains(this.lastFocusedElement)) {
      this.lastFocusedElement.focus();
    }

    this.config.cartOpen = false;
  }
}
